import type { ActivityEvent, ActivityDigest } from "@/hooks/useProjectActivity";

// ---------------------------------------------------------------------------
// Categories
// ---------------------------------------------------------------------------

export type ActivityCategory = "development" | "pipeline" | "costs";

const EVENT_TYPE_TO_CATEGORY: Record<string, ActivityCategory> = {
  work_lifecycle: "development",
  session_topic: "development",
  session_closed: "development",
  session_started: "development",
  pipeline_phase_completed: "pipeline",
  phase_finish: "pipeline",
  token_usage_daily: "costs",
  story_cost_summary: "costs",
  token_usage: "costs",
};

export function categorize(eventType: string): ActivityCategory {
  return EVENT_TYPE_TO_CATEGORY[eventType] ?? "development";
}

export const CATEGORY_META: Record<
  ActivityCategory,
  { label: string; colorClass: string; bgClass: string; borderClass: string }
> = {
  development: {
    label: "Development",
    colorClass: "text-blue-400",
    bgClass: "bg-blue-500/10",
    borderClass: "border-blue-500/20",
  },
  pipeline: {
    label: "Pipeline",
    colorClass: "text-emerald-400",
    bgClass: "bg-emerald-500/10",
    borderClass: "border-emerald-500/20",
  },
  costs: {
    label: "Costs",
    colorClass: "text-amber-400",
    bgClass: "bg-amber-500/10",
    borderClass: "border-amber-500/20",
  },
};

// ---------------------------------------------------------------------------
// Digest summary metrics
// ---------------------------------------------------------------------------

export interface DigestSummary {
  totalEvents: number;
  storiesActive: number;
  sessionsToday: number;
  pipelinePhases: number;
}

export function computeDigestSummary(
  digest: ActivityDigest | null,
  events: ActivityEvent[],
  total: number,
): DigestSummary {
  if (digest) {
    return {
      totalEvents: total,
      storiesActive: Object.values(digest.workflow).reduce((a, b) => a + b, 0),
      sessionsToday: Object.values(digest.sessions).reduce((a, b) => a + b, 0),
      pipelinePhases: Object.values(digest.pipeline).reduce((a, b) => a + b, 0),
    };
  }

  return {
    totalEvents: total || events.length,
    storiesActive: events.filter((e) => e.event_type === "work_lifecycle").length,
    sessionsToday: events.filter(
      (e) => e.event_type === "session_topic" || e.event_type === "session_closed",
    ).length,
    pipelinePhases: events.filter(
      (e) => e.event_type === "pipeline_phase_completed" || e.event_type === "phase_finish",
    ).length,
  };
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

export function formatCost(usd: number): string {
  if (usd >= 0.01) return `$${usd.toFixed(2)}`;
  return `$${usd.toFixed(3)}`;
}

export function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  const hours = Math.floor(seconds / 3600);
  const mins = Math.round((seconds % 3600) / 60);
  return mins > 0 ? `${String(hours)}h ${String(mins)}m` : `${String(hours)}h`;
}
